import usePrivateAxios from '@/common/api/usePrivateAxios'
import { ErrorResponse } from '@/common/types/errorResponse'
import { TicketData } from '@/common/types/support/ticket.type'
import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { updateUserTicketsRelationType } from '@/features/support/internal/services/updateUserTicketsRelationType'
import { SupportTicketAvailableUserWithRelationType } from '@/features/support/internal/types/supportTicketAvailableUser.type'
import { useMutation } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

interface UseUpdateInternalSupportTicketProps {
  supportTickerId?: string
  onSuccess: (response: AxiosResponse) => void
  onError: (error: ErrorResponse) => void
}

export const useUpdateInternalSupportTicket = ({
  supportTickerId,
  onSuccess,
  onError,
}: UseUpdateInternalSupportTicketProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['createInternalSupportTicket'],
    mutationFn: async ({
      users,
      supportTickets,
      supportTicketData,
    }: {
      users: SupportTicketAvailableUserWithRelationType[]
      supportTickets: TicketData[]
      supportTicketData?: TicketData
    }) => {
      const payload = {
        user_tickets_attributes: [
          ...getRailsUpdate(
            supportTicketData?.user_tickets || [],
            users || [],
            (x) => x.user.id,
            (x) => x.id,
            mapToDeletion,
            (item: SupportTicketAvailableUserWithRelationType) => ({
              user_id: item.id,
              user_type: 'Admin::User',
              relation_type: item.relationType,
            }),
          ),
          ...updateUserTicketsRelationType({ supportTicketData, users }),
        ],
        outgoing_ticket_linkings_attributes: getRailsUpdate(
          supportTicketData?.outgoing_ticket_linkings || [],
          supportTickets || [],
          (x) => x.target_ticket.id,
          (x) => x.id,
          mapToDeletion,
          (item: TicketData) => ({ target_ticket_id: item.id }),
        ),
      }

      return privateAxios.put(`/support/tickets/${supportTickerId}/update_internal_ticket`, { support_ticket: payload })
    },
    onSuccess,
    onError,
  })
}
